import React from 'react';
import { Button, ButtonProps, CircularProgress } from '@mui/material';

/**
 * Design System Color Constants
 */
const COLORS = {
  ACCENT: '#005e5d',
  ACCENT_DARK: '#004d4c',
  ACCENT_LIGHT: '#005e5d14',
  SURFACE_SECONDARY: '#F2F3F5',
  BORDER: '#E6E7EB',
  TEXT_PRIMARY: '#333333',
  TEXT_SECONDARY: '#666666',
  TEXT_TERTIARY: '#999999',
  DESTRUCTIVE: '#D32F2F',
  DESTRUCTIVE_DARK: '#B71C1C',
  DESTRUCTIVE_LIGHT: '#F9DEDC',
  SUCCESS: '#49CC90',
  SUCCESS_DARK: '#2E8B6A',
  SUCCESS_LIGHT: '#E8F5E9',
  WARNING: '#FCA130',
  WARNING_DARK: '#D68000',
  WARNING_LIGHT: '#FFF3CD',
};

export interface BaseButtonProps extends ButtonProps {
  /**
   * Button color variant
   * @default 'primary'
   */
  colorVariant?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
  /**
   * Show loading state with spinner
   * @default false
   */
  isLoading?: boolean;
  /**
   * Loading label text
   */
  loadingLabel?: string;
}

/**
 * BaseButton Component
 *
 * Premium button component following the Pulse API design system.
 * Supports multiple variants (contained, outlined, text) and color options.
 *
 * @example
 * ```tsx
 * // Primary contained button
 * <BaseButton variant="contained" color="primary">
 *   Create New
 * </BaseButton>
 *
 * // Secondary outlined button
 * <BaseButton variant="outlined">
 *   Cancel
 * </BaseButton>
 *
 * // Danger button with loading state
 * <BaseButton
 *   variant="contained"
 *   colorVariant="error"
 *   isLoading={isSaving}
 *   loadingLabel="Deleting..."
 * >
 *   Delete
 * </BaseButton>
 * ```
 */
export const BaseButton = React.forwardRef<HTMLButtonElement, BaseButtonProps>(
  (
    {
      variant = 'contained',
      colorVariant = 'primary',
      color,
      isLoading = false,
      loadingLabel,
      disabled,
      startIcon,
      endIcon,
      children,
      size = 'medium',
      sx = {},
      ...props
    },
    ref
  ) => {
    /**
     * Determine color scheme based on colorVariant
     */
    const getColorScheme = (cv: typeof colorVariant, v: typeof variant) => {
      const schemes = {
        primary: {
          contained: {
            backgroundColor: COLORS.ACCENT,
            color: '#FFFFFF',
            border: 'none',
            '&:hover': {
              backgroundColor: COLORS.ACCENT_DARK,
              boxShadow: `0 4px 12px ${COLORS.ACCENT}30`,
            },
            '&:active': {
              backgroundColor: COLORS.ACCENT_DARK,
            },
            '&:disabled': {
              backgroundColor: COLORS.SURFACE_SECONDARY,
              color: COLORS.TEXT_TERTIARY,
            },
          },
          outlined: {
            backgroundColor: 'transparent',
            color: COLORS.ACCENT,
            border: `1.5px solid ${COLORS.ACCENT}`,
            '&:hover': {
              backgroundColor: COLORS.ACCENT_LIGHT,
              border: `1.5px solid ${COLORS.ACCENT}`,
            },
            '&:active': {
              backgroundColor: COLORS.ACCENT_LIGHT,
            },
            '&:disabled': {
              border: `1.5px solid ${COLORS.BORDER}`,
              color: COLORS.TEXT_TERTIARY,
            },
          },
          text: {
            backgroundColor: 'transparent',
            color: COLORS.ACCENT,
            '&:hover': {
              backgroundColor: COLORS.ACCENT_LIGHT,
            },
            '&:active': {
              backgroundColor: COLORS.ACCENT_LIGHT,
            },
            '&:disabled': {
              color: COLORS.TEXT_TERTIARY,
            },
          },
        },
        secondary: {
          contained: {
            backgroundColor: COLORS.SURFACE_SECONDARY,
            color: COLORS.TEXT_PRIMARY,
            border: `1px solid ${COLORS.BORDER}`,
            '&:hover': {
              backgroundColor: COLORS.BORDER,
              boxShadow: `0 2px 8px rgba(0,0,0,0.06)`,
            },
            '&:active': {
              backgroundColor: COLORS.BORDER,
            },
            '&:disabled': {
              backgroundColor: COLORS.SURFACE_SECONDARY,
              color: COLORS.TEXT_TERTIARY,
              border: `1px solid ${COLORS.BORDER}`,
            },
          },
          outlined: {
            backgroundColor: 'transparent',
            color: COLORS.TEXT_PRIMARY,
            border: `1.5px solid ${COLORS.BORDER}`,
            '&:hover': {
              backgroundColor: COLORS.SURFACE_SECONDARY,
              border: `1.5px solid ${COLORS.BORDER}`,
            },
            '&:active': {
              backgroundColor: COLORS.SURFACE_SECONDARY,
            },
            '&:disabled': {
              border: `1.5px solid ${COLORS.BORDER}`,
              color: COLORS.TEXT_TERTIARY,
            },
          },
          text: {
            backgroundColor: 'transparent',
            color: COLORS.TEXT_PRIMARY,
            '&:hover': {
              backgroundColor: COLORS.SURFACE_SECONDARY,
            },
            '&:active': {
              backgroundColor: COLORS.BORDER,
            },
            '&:disabled': {
              color: COLORS.TEXT_TERTIARY,
            },
          },
        },
        success: {
          contained: {
            backgroundColor: COLORS.SUCCESS,
            color: '#FFFFFF',
            border: 'none',
            '&:hover': {
              backgroundColor: COLORS.SUCCESS_DARK,
              boxShadow: `0 4px 12px ${COLORS.SUCCESS}30`,
            },
            '&:active': {
              backgroundColor: COLORS.SUCCESS_DARK,
            },
            '&:disabled': {
              backgroundColor: COLORS.SURFACE_SECONDARY,
              color: COLORS.TEXT_TERTIARY,
            },
          },
          outlined: {
            backgroundColor: 'transparent',
            color: COLORS.SUCCESS,
            border: `1.5px solid ${COLORS.SUCCESS}`,
            '&:hover': {
              backgroundColor: COLORS.SUCCESS_LIGHT,
              border: `1.5px solid ${COLORS.SUCCESS}`,
            },
            '&:active': {
              backgroundColor: COLORS.SUCCESS_LIGHT,
            },
            '&:disabled': {
              border: `1.5px solid ${COLORS.BORDER}`,
              color: COLORS.TEXT_TERTIARY,
            },
          },
          text: {
            backgroundColor: 'transparent',
            color: COLORS.SUCCESS,
            '&:hover': {
              backgroundColor: COLORS.SUCCESS_LIGHT,
            },
            '&:active': {
              backgroundColor: COLORS.SUCCESS_LIGHT,
            },
            '&:disabled': {
              color: COLORS.TEXT_TERTIARY,
            },
          },
        },
        warning: {
          contained: {
            backgroundColor: COLORS.WARNING,
            color: '#FFFFFF',
            border: 'none',
            '&:hover': {
              backgroundColor: COLORS.WARNING_DARK,
              boxShadow: `0 4px 12px ${COLORS.WARNING}30`,
            },
            '&:active': {
              backgroundColor: COLORS.WARNING_DARK,
            },
            '&:disabled': {
              backgroundColor: COLORS.SURFACE_SECONDARY,
              color: COLORS.TEXT_TERTIARY,
            },
          },
          outlined: {
            backgroundColor: 'transparent',
            color: COLORS.WARNING,
            border: `1.5px solid ${COLORS.WARNING}`,
            '&:hover': {
              backgroundColor: COLORS.WARNING_LIGHT,
              border: `1.5px solid ${COLORS.WARNING}`,
            },
            '&:active': {
              backgroundColor: COLORS.WARNING_LIGHT,
            },
            '&:disabled': {
              border: `1.5px solid ${COLORS.BORDER}`,
              color: COLORS.TEXT_TERTIARY,
            },
          },
          text: {
            backgroundColor: 'transparent',
            color: COLORS.WARNING,
            '&:hover': {
              backgroundColor: COLORS.WARNING_LIGHT,
            },
            '&:active': {
              backgroundColor: COLORS.WARNING_LIGHT,
            },
            '&:disabled': {
              color: COLORS.TEXT_TERTIARY,
            },
          },
        },
        error: {
          contained: {
            backgroundColor: COLORS.DESTRUCTIVE,
            color: '#FFFFFF',
            border: 'none',
            '&:hover': {
              backgroundColor: COLORS.DESTRUCTIVE_DARK,
              boxShadow: `0 4px 12px ${COLORS.DESTRUCTIVE}30`,
            },
            '&:active': {
              backgroundColor: COLORS.DESTRUCTIVE_DARK,
            },
            '&:disabled': {
              backgroundColor: COLORS.SURFACE_SECONDARY,
              color: COLORS.TEXT_TERTIARY,
            },
          },
          outlined: {
            backgroundColor: 'transparent',
            color: COLORS.DESTRUCTIVE,
            border: `1.5px solid ${COLORS.DESTRUCTIVE}`,
            '&:hover': {
              backgroundColor: COLORS.DESTRUCTIVE_LIGHT,
              border: `1.5px solid ${COLORS.DESTRUCTIVE}`,
            },
            '&:active': {
              backgroundColor: COLORS.DESTRUCTIVE_LIGHT,
            },
            '&:disabled': {
              border: `1.5px solid ${COLORS.BORDER}`,
              color: COLORS.TEXT_TERTIARY,
            },
          },
          text: {
            backgroundColor: 'transparent',
            color: COLORS.DESTRUCTIVE,
            '&:hover': {
              backgroundColor: COLORS.DESTRUCTIVE_LIGHT,
            },
            '&:active': {
              backgroundColor: COLORS.DESTRUCTIVE_LIGHT,
            },
            '&:disabled': {
              color: COLORS.TEXT_TERTIARY,
            },
          },
        },
      };

      return schemes[cv]?.[v as keyof typeof schemes[typeof cv]] || schemes.primary.contained;
    };

    const colorScheme = getColorScheme(colorVariant, variant ?? 'contained');

    return (
      <Button
        ref={ref}
        variant={variant as any}
        disabled={disabled || isLoading}
        startIcon={
          isLoading ? (
            <CircularProgress
              size={size === 'small' ? 14 : size === 'large' ? 18 : 16}
              thickness={5}
              sx={{ color: 'currentColor' }}
            />
          ) : (
            startIcon
          )
        }
        endIcon={endIcon}
        sx={{
          ...colorScheme,
          // Size variants
          ...(size === 'small' && {
            padding: '6px 12px',
            fontSize: '12px',
            fontWeight: 600,
            borderRadius: '6px',
            minHeight: '28px',
            textTransform: 'none',
          }),
          ...(size === 'medium' && {
            padding: '8px 16px',
            fontSize: '13px',
            fontWeight: 600,
            borderRadius: '8px',
            minHeight: '36px',
            textTransform: 'none',
          }),
          ...(size === 'large' && {
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: 600,
            borderRadius: '8px',
            minHeight: '44px',
            textTransform: 'none',
          }),

          // Shared styles
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          fontFamily: '"Manrope", sans-serif',
          letterSpacing: '-0.01em',
          boxShadow: 'none',
          boxSizing: 'border-box',

          // Disable default MUI styles
          '&.MuiButton-root': {
            textTransform: 'none',
            boxShadow: 'none',
            boxSizing: 'border-box',
          },

          ...sx,
        }}
        {...props}
      >
        {isLoading && loadingLabel ? loadingLabel : children}
      </Button>
    );
  }
);

BaseButton.displayName = 'BaseButton';

export default BaseButton;
